import { ClearAccountForm } from '@/features/SalesforceAccounts/components/ClearAccountForm'
import { LinkAccountForm } from '@/features/SalesforceAccounts/components/LinkAccountForm'
import { RepointAccountForm } from '@/features/SalesforceAccounts/components/RepointAccountForm'
import type { SalesforceLinkFormProps } from '@/features/SalesforceAccounts/components/salesforceLinkFormProps'
import { Dialog, Portal } from '@chakra-ui/react'
import type { FC } from 'react'

export type SalesforceLinkActionId = 'link' | 'repoint' | 'clear'

const FORMS: Record<SalesforceLinkActionId, FC<SalesforceLinkFormProps>> = {
  link: LinkAccountForm,
  repoint: RepointAccountForm,
  clear: ClearAccountForm,
}

type Props = {
  action: SalesforceLinkActionId | null
  orgId: string
  /**
   * The stored link, read once by the card. Passed down rather than re-read
   * here so there is a single origin for the compare-and-swap token.
   */
  currentAccountId?: string
  onClose: () => void
  onReRead: () => Promise<boolean>
}

/**
 * Dispatcher for the three link actions. The dialog owns the chrome-level open
 * state; each form owns its own fields, conflict panel and submit rule.
 */
export function SalesforceLinkActionDialog({
  action,
  orgId,
  currentAccountId,
  onClose,
  onReRead,
}: Readonly<Props>) {
  const Form = action ? FORMS[action] : null

  return (
    <Dialog.Root
      open={action !== null}
      onOpenChange={(details) => {
        if (!details.open) {
          onClose()
        }
      }}
      size="md"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            {Form ? (
              <Form
                orgId={orgId}
                currentAccountId={currentAccountId}
                onSuccess={onClose}
                onReRead={onReRead}
              />
            ) : null}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
